import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit, Trash2, X, Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import ImageUploader from '../../components/ImageUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyPost = {
  slug: '',
  category: 'news',
  author: '',
  images: [''],
  translations: {
    sk: { title: '', excerpt: '', content: '' },
    en: { title: '', excerpt: '', content: '' },
    de: { title: '', excerpt: '', content: '' },
    cs: { title: '', excerpt: '', content: '' },
    ru: { title: '', excerpt: '', content: '' }
  }
};

const AdminBlog = () => {
  const { t, i18n } = useTranslation();
  const { token, user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('sk');

  useEffect(() => {
    fetchPosts();
  }, []);

  const uiLang = (i18n.language || 'sk').split('-')[0];
  // For row display fallback through languages
  const pickTitle = (post) => {
    const tr = post.translations || {};
    return tr[uiLang]?.title || tr.sk?.title || tr.en?.title || '—';
  };
  const pickExcerpt = (post) => {
    const tr = post.translations || {};
    return tr[uiLang]?.excerpt || tr.sk?.excerpt || tr.en?.excerpt || '';
  };

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      toast.error(t('admin.blog_page.fetch_error', 'Chyba pri načítaní článkov'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm(t('admin.blog_page.delete_confirm', 'Naozaj chcete zmazať tento článok?'))) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/blog/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setPosts(posts.filter(p => p.id !== postId));
        toast.success(t('admin.blog_page.deleted', 'Článok bol zmazaný'));
      } else {
        toast.error(t('admin.blog_page.delete_error', 'Chyba pri mazaní článku'));
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error(t('admin.blog_page.delete_error', 'Chyba pri mazaní článku'));
    }
  };

  const handleSave = async (post) => {
    setSaving(true);
    try {
      const url = isCreating
        ? `${API_URL}/api/admin/blog`
        : `${API_URL}/api/admin/blog/${post.id}`;

      const postData = {
        ...post,
        author: post.author || user?.name || 'Admin'
      };

      const response = await fetch(url, {
        method: isCreating ? 'POST' : 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
      });

      if (response.ok) {
        fetchPosts();
        setEditingPost(null);
        setIsCreating(false);
        toast.success(isCreating
          ? t('admin.blog_page.created', 'Článok bol vytvorený')
          : t('admin.blog_page.updated', 'Článok bol aktualizovaný'));
      } else {
        const err = await response.json();
        toast.error(err.detail || t('admin.blog_page.save_error', 'Chyba pri ukladaní'));
      }
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error(t('admin.blog_page.save_error_full', 'Chyba pri ukladaní článku'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingPost({ ...emptyPost, author: user?.name || 'Admin' });
    setActiveTab('sk');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(uiLang === 'ru' ? 'ru-RU' : uiLang === 'en' ? 'en-US' : 'sk-SK');
  };

  const languages = [
    { code: 'sk', label: 'Slovenčina' },
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' },
    { code: 'cs', label: 'Čeština' },
    { code: 'ru', label: 'Русский' }
  ];

  const categoryLabel = (cat) => {
    const map = {
      news: t('admin.blog_page.cat_news', 'Novinky'),
      tips: t('admin.blog_page.cat_tips', 'Tipy'),
      guides: t('admin.blog_page.cat_guides', 'Návody'),
    };
    return map[cat] || cat;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold" data-testid="admin-blog-title">{t('admin.blog_page.title', 'Blog')}</h1>
        <Button
          className="bg-orange-500 hover:bg-orange-600"
          onClick={handleCreate}
          data-testid="add-blog-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('admin.blog_page.add', 'Pridať článok')}
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t('admin.blog_page.empty', 'Žiadne články')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.blog_page.col_article', 'Článok')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.blog_page.col_category', 'Kategória')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.blog_page.col_author', 'Autor')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.blog_page.col_date', 'Dátum')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.blog_page.col_actions', 'Akcie')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {posts.map(post => (
                  <tr key={post.id} className="hover:bg-gray-50" data-testid={`blog-row-${post.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <img
                          src={post.images?.[0] || 'https://via.placeholder.com/50'}
                          alt=""
                          className="w-12 h-12 rounded object-cover mr-4"
                        />
                        <div>
                          <div className="font-medium">{pickTitle(post)}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {pickExcerpt(post)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-sm">
                        {categoryLabel(post.category)}
                      </span>
                    </td>
                    <td className="px-6 py-4">{post.author}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(post.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setIsCreating(false); setEditingPost(post); setActiveTab('sk'); }}
                          data-testid={`edit-blog-${post.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 hover:text-red-600"
                          onClick={() => handleDelete(post.id)}
                          data-testid={`delete-blog-${post.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {editingPost && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold">
                {isCreating
                  ? t('admin.blog_page.new_article', 'Nový článok')
                  : t('admin.blog_page.edit_article', 'Upraviť článok')}
              </h2>
              <button onClick={() => { setEditingPost(null); setIsCreating(false); }}>
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('admin.blog_page.slug', 'Slug (URL)')}</label>
                  <Input
                    value={editingPost.slug || ''}
                    onChange={(e) => setEditingPost({ ...editingPost, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    placeholder="moj-clanok"
                    data-testid="blog-slug-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('admin.blog_page.category', 'Kategória')}</label>
                  <select
                    className="w-full px-3 py-2 border rounded-md"
                    value={editingPost.category || 'news'}
                    onChange={(e) => setEditingPost({ ...editingPost, category: e.target.value })}
                    data-testid="blog-category-select"
                  >
                    <option value="news">{t('admin.blog_page.cat_news', 'Novinky')}</option>
                    <option value="tips">{t('admin.blog_page.cat_tips', 'Tipy')}</option>
                    <option value="guides">{t('admin.blog_page.cat_guides', 'Návody')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.blog_page.author', 'Autor')}</label>
                <Input
                  value={editingPost.author || ''}
                  onChange={(e) => setEditingPost({ ...editingPost, author: e.target.value })}
                  placeholder={t('admin.blog_page.author_ph', 'Meno autora')}
                  data-testid="blog-author-input"
                />
              </div>

              {/* Image Upload */}
              <ImageUploader
                token={token}
                currentImage={editingPost.images?.[0] || ''}
                onImageUploaded={(url) => setEditingPost({ ...editingPost, images: [url] })}
                label={t('admin.blog_page.main_image', 'Hlavný obrázok článku')}
              />

              {/* Language Tabs */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">{t('admin.blog_page.content', 'Obsah článku')}</h3>
                <div className="flex gap-2 mb-4 flex-wrap">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => setActiveTab(lang.code)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                        activeTab === lang.code
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('admin.blog_page.field_title', 'Názov')} ({activeTab.toUpperCase()})</label>
                    <Input
                      value={editingPost.translations?.[activeTab]?.title || ''}
                      onChange={(e) => setEditingPost({
                        ...editingPost,
                        translations: {
                          ...editingPost.translations,
                          [activeTab]: { ...editingPost.translations?.[activeTab], title: e.target.value }
                        }
                      })}
                      placeholder={t('admin.blog_page.field_title_ph', 'Názov článku')}
                      data-testid={`blog-title-${activeTab}`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('admin.blog_page.field_excerpt', 'Krátky popis')} ({activeTab.toUpperCase()})</label>
                    <Input
                      value={editingPost.translations?.[activeTab]?.excerpt || ''}
                      onChange={(e) => setEditingPost({
                        ...editingPost,
                        translations: {
                          ...editingPost.translations,
                          [activeTab]: { ...editingPost.translations?.[activeTab], excerpt: e.target.value }
                        }
                      })}
                      placeholder={t('admin.blog_page.field_excerpt_ph', 'Krátky popis článku')}
                      data-testid={`blog-excerpt-${activeTab}`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('admin.blog_page.field_content', 'Obsah')} ({activeTab.toUpperCase()})</label>
                    <textarea
                      className="w-full px-3 py-2 border rounded-md min-h-[200px]"
                      value={editingPost.translations?.[activeTab]?.content || ''}
                      onChange={(e) => setEditingPost({
                        ...editingPost,
                        translations: {
                          ...editingPost.translations,
                          [activeTab]: { ...editingPost.translations?.[activeTab], content: e.target.value }
                        }
                      })}
                      placeholder={t('admin.blog_page.field_content_ph', 'Plný text článku...')}
                      data-testid={`blog-content-${activeTab}`}
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => { setEditingPost(null); setIsCreating(false); }}>
                  {t('common.cancel', 'Zrušiť')}
                </Button>
                <Button
                  className="bg-orange-500 hover:bg-orange-600"
                  onClick={() => handleSave(editingPost)}
                  disabled={saving}
                  data-testid="save-blog-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="animate-spin mr-2 h-4 w-4" />
                      {t('admin.blog_page.saving', 'Ukladám...')}
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {isCreating
                        ? t('admin.blog_page.create_btn', 'Vytvoriť')
                        : t('admin.blog_page.save_btn', 'Uložiť')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
